import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AGING_SCALE, CHROME, MARKS, SERIES } from "@/utils/receivablesChartTokens";

/**
 * Renders a chart spec produced by the assistant.
 *
 * The spec's `data` comes from a SQL tool result server-side — this
 * component only decides how to draw it.
 */

export type AssistantChartSpec = {
  chartType: "bar" | "horizontal_bar" | "line" | "area" | "pie";
  title: string;
  xKey: string;
  seriesKeys: string[];
  data: Array<Record<string, string | number | null>>;
  valueSuffix?: string | null;
  sourceTool?: string;
};

// Categorical slots in fixed order — a series keeps its color regardless of
// how many other series are present.
const SERIES_COLORS = [
  SERIES.primary,
  SERIES.secondary,
  AGING_SCALE.d31_60,
  "#4a3aa7",
  AGING_SCALE.d90_plus,
  "#e87ba4",
];

function humanize(key: string): string {
  return key
    .replace(/[._]/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function compact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${Math.round(value / 1_000)}K`;
  return String(Math.round(value));
}

function AssistantTooltip({
  active,
  payload,
  label,
  valueSuffix,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ name?: string; value?: number; color?: string }>;
  label?: string | number;
  valueSuffix?: string | null;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-[#E5E7EB] bg-white/95 px-2.5 py-1.5 text-xs shadow-lg backdrop-blur-sm">
      {label != null ? (
        <p className="mb-1 font-medium text-[#111827]">{String(label)}</p>
      ) : null}
      {payload.map((entry) => (
        <div
          key={String(entry.name)}
          className="flex items-center justify-between gap-4"
        >
          <span className="inline-flex items-center gap-1.5 text-[#52514E]">
            <span
              className="h-2 w-2 rounded-sm"
              style={{ backgroundColor: entry.color }}
              aria-hidden
            />
            {humanize(String(entry.name))}
          </span>
          <span className="font-medium tabular-nums text-[#111827]">
            {typeof entry.value === "number"
              ? entry.value.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })
              : String(entry.value)}
            {valueSuffix ? ` ${valueSuffix}` : ""}
          </span>
        </div>
      ))}
    </div>
  );
}

export function AssistantChart({ spec }: { spec: AssistantChartSpec }) {
  const { chartType, xKey, seriesKeys, data, valueSuffix } = spec;
  const showLegend = seriesKeys.length > 1;

  const axisTick = { fontSize: 10, fill: CHROME.textMuted };
  const grid = (
    <CartesianGrid
      stroke={CHROME.gridline}
      strokeWidth={1}
      vertical={chartType === "horizontal_bar"}
      horizontal={chartType !== "horizontal_bar"}
    />
  );
  const tooltip = (
    <Tooltip
      content={<AssistantTooltip valueSuffix={valueSuffix} />}
      cursor={{ fill: "rgba(17,24,39,0.04)" }}
    />
  );
  const legend = showLegend ? (
    <Legend
      wrapperStyle={{ fontSize: 11 }}
      formatter={(value: string) => humanize(value)}
    />
  ) : null;

  if (chartType === "pie") {
    const valueKey = seriesKeys[0];
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey={valueKey}
            nameKey={xKey}
            innerRadius="45%"
            outerRadius="75%"
            stroke={CHROME.surface}
            strokeWidth={2}
            isAnimationActive={false}
          >
            {data.map((row, index) => (
              <Cell
                key={String(row[xKey]) || index}
                fill={SERIES_COLORS[index % SERIES_COLORS.length]}
              />
            ))}
          </Pie>
          {tooltip}
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === "horizontal_bar") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ left: 4, right: 12 }}>
          {grid}
          <XAxis
            type="number"
            tick={axisTick}
            tickLine={false}
            axisLine={{ stroke: CHROME.axis }}
            tickFormatter={compact}
          />
          <YAxis
            type="category"
            dataKey={xKey}
            width={104}
            tick={{ fontSize: 10, fill: CHROME.textSecondary }}
            tickLine={false}
            axisLine={false}
          />
          {tooltip}
          {legend}
          {seriesKeys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              fill={SERIES_COLORS[index % SERIES_COLORS.length]}
              maxBarSize={MARKS.maxBarThickness}
              radius={[0, 4, 4, 0]}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === "line" || chartType === "area") {
    const ChartComponent = chartType === "area" ? AreaChart : LineChart;
    return (
      <ResponsiveContainer width="100%" height="100%">
        <ChartComponent data={data} margin={{ top: 6, right: 10, left: 0 }}>
          {grid}
          <XAxis
            dataKey={xKey}
            tick={axisTick}
            tickLine={false}
            axisLine={{ stroke: CHROME.axis }}
            interval="preserveStartEnd"
            minTickGap={12}
          />
          <YAxis
            tick={axisTick}
            tickLine={false}
            axisLine={false}
            width={46}
            tickFormatter={compact}
          />
          {tooltip}
          {legend}
          {seriesKeys.map((key, index) =>
            chartType === "area" ? (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={SERIES_COLORS[index % SERIES_COLORS.length]}
                strokeWidth={MARKS.lineWidth}
                fill={SERIES_COLORS[index % SERIES_COLORS.length]}
                fillOpacity={MARKS.areaOpacity}
                isAnimationActive={false}
              />
            ) : (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={SERIES_COLORS[index % SERIES_COLORS.length]}
                strokeWidth={MARKS.lineWidth}
                dot={{ r: 2.5 }}
                isAnimationActive={false}
              />
            ),
          )}
        </ChartComponent>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 6, right: 10, left: 0 }}>
        {grid}
        <XAxis
          dataKey={xKey}
          tick={axisTick}
          tickLine={false}
          axisLine={{ stroke: CHROME.axis }}
          interval="preserveStartEnd"
          minTickGap={8}
        />
        <YAxis
          tick={axisTick}
          tickLine={false}
          axisLine={false}
          width={46}
          tickFormatter={compact}
        />
        {tooltip}
        {legend}
        {seriesKeys.map((key, index) => (
          <Bar
            key={key}
            dataKey={key}
            fill={SERIES_COLORS[index % SERIES_COLORS.length]}
            maxBarSize={MARKS.maxBarThickness}
            radius={MARKS.barRadius}
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
